import moment from 'moment';
import { getMonthsRanges } from 'src/common/helpers/date.helpers';
import lowerFirst from 'lodash/lowerFirst';
import { IMonthlyWorkload } from '../../dasboard.interfaces';

const getTotalWorkingHoursPerMonth = (
  employeesWorkingHours: any,
  workloadList: any,
  year: number
) => {
  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const monthCounts: any = {};

  for (let month = 0; month < 12; month++) {
    const currentMonth = moment([year, month]);
    const monthName = currentMonth.format('MMMM');
    const currentDay = currentMonth.clone().startOf('month');
    let total = 0;

    const dayCounts: any = {
      Sunday: 0,
      Monday: 0,
      Tuesday: 0,
      Wednesday: 0,
      Thursday: 0,
      Friday: 0,
      Saturday: 0,
    };

    while (currentDay.month() === month) {
      const dayOfWeek = currentDay.format('dddd');
      dayCounts[dayOfWeek]++;
      currentDay.add(1, 'day');
    }

    monthCounts[monthName] = dayCounts;

    employeesWorkingHours.forEach((record: any) => {
      let totalHrs = 0;

      for (const dayName of dayNames) {
        if (record[lowerFirst(dayName)]) {
          const dayData = record[lowerFirst(dayName)];
          if (dayData && dayData.from && dayData.to) {
            const workingHours =
              (moment(dayData.to).hours() - moment(dayData.from).hours() - record.breakTime) *
              monthCounts[monthName][dayName];
            totalHrs += workingHours;
          }
        }
      }

      total = !isNaN(totalHrs) ? total + totalHrs : total + 0;

      monthCounts[monthName].totalWorkingHours = total;
    });

    const absences = workloadList.filter(
      (record: any) =>
        record.date.isSameOrAfter(currentMonth.startOf('month')) &&
        record.date.isSameOrBefore(currentMonth.endOf('month')) &&
        record.entries.some((ent: any) => ent.type === 'ABSENCE_REASON')
    );

    monthCounts[monthName].absences = absences;

    const plannedEntryHours = absences.reduce(
      (total: number, absence: any) =>
        total +
        (absence.entries.find((ent: any) => ent.type === 'ABSENCE_REASON')?.plannedEntryHours || 0),
      0
    );

    monthCounts[monthName].totalWorkingHours -= plannedEntryHours;
  }

  return monthCounts;
};

export const getMonthlyWorkload = (
  workloadList: any[],
  timeStripes: any[],
  employeesWorkingHours: any,
  year: number
) => {
  const monthsRanges = getMonthsRanges(year);

  const monthlyWorkload: IMonthlyWorkload = {};
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const workingHours = getTotalWorkingHoursPerMonth(employeesWorkingHours, workloadList, year);

  monthNames.forEach((monthName, index) => {
    monthlyWorkload[monthName] = workloadList
      .filter(
        record => record.date >= monthsRanges[index].start && record.date <= monthsRanges[index].end
      )
      .reduce(
        (acc, record) => {
          const confirmedTimeStripes = timeStripes.filter(
            stripe =>
              stripe.confirmed === true &&
              stripe.startDate >= monthsRanges[index].start.toISOString() &&
              stripe.endDate <= monthsRanges[index].end.toISOString()
          );
          const absence = record.entries.find((ent: any) => ent.type === 'ABSENCE_REASON');

          acc.capacity = workingHours[monthName].totalWorkingHours || 0;
          acc.planned += absence ? record.planned - absence.plannedEntryHours : record.planned || 0;
          acc.confirmed =
            confirmedTimeStripes.length > 0
              ? confirmedTimeStripes.reduce((sum: number, rec: any) => sum + rec.plannedHours, 0)
              : 0;
          acc.plannedPercents = ((100 * acc.planned) / acc.capacity).toFixed(2);
          acc.confirmedPercents = ((100 * acc.confirmed) / acc.capacity).toFixed(2);
          acc.plannedWithPercents =
            acc.planned !== 0 ? `${acc.planned.toFixed(2)} - ${acc.plannedPercents}%` : '';
          acc.confirmedWithPercents =
            acc.confirmed !== 0 ? `${acc.confirmed.toFixed(2)} - ${acc.confirmedPercents}%` : '';

          return acc;
        },
        {
          monthName,
          capacity: workingHours[monthName].totalWorkingHours || 0,
          planned: 0,
          confirmed: 0,
        }
      );
  });

  return monthlyWorkload;
};

// getYears :: _ -> { CurrentYear, [Year] }
export const getYears = () => {
  const years: number[] = [];
  const currentYear = moment().year();

  for (let i = 0; i <= currentYear - 2021; i++) {
    years.push(currentYear - i);
  }

  return {
    currentYear,
    years,
  };
};
